// Producer-chosen example videos on the general briefing.
//
// A producer adds links (YouTube, Vimeo, Instagram, Facebook) with a
// note on why it's a reference and an optional start time. They live in
// `briefings.video_examples` jsonb as an ordered array of
// { id, url, note, start_seconds }.
//
// Crew open them in the provider's own app (no in-app player: that
// would need a native dependency). This module owns the type, the
// defensive parser and the URL logic once, shared between the producer
// editor, the review step and the crew app.

export type VideoProvider = "youtube" | "vimeo" | "instagram" | "facebook";

export interface BriefingVideoExample {
  /** Stable key for React lists. */
  id: string;
  /** The link as the producer pasted it. */
  url: string;
  /** Why this video is a reference. */
  note: string;
  /** Second to start at, or null for the beginning. */
  start_seconds: number | null;
}

export const VIDEO_PROVIDER_LABELS: Record<VideoProvider, string> = {
  youtube: "YouTube",
  vimeo: "Vimeo",
  instagram: "Instagram",
  facebook: "Facebook",
};

/** Mirrors the CHECK constraint on briefings.video_examples. */
export const MAX_VIDEO_EXAMPLES = 20;

function parseHttpUrl(raw: string): URL | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(withScheme);
    return url.protocol === "https:" || url.protocol === "http:" ? url : null;
  } catch {
    return null;
  }
}

function hostMatches(host: string, domain: string): boolean {
  return host === domain || host.endsWith(`.${domain}`);
}

function providerOf(url: URL): VideoProvider | null {
  const host = url.hostname.toLowerCase();
  if (hostMatches(host, "youtube.com") || host === "youtu.be") return "youtube";
  if (hostMatches(host, "vimeo.com")) return "vimeo";
  if (hostMatches(host, "instagram.com")) return "instagram";
  if (hostMatches(host, "facebook.com") || host === "fb.watch") return "facebook";
  return null;
}

/** Which supported service a link points to, or null when unsupported. */
export function detectVideoProvider(raw: string): VideoProvider | null {
  const url = parseHttpUrl(raw);
  return url ? providerOf(url) : null;
}

/** Instagram and Facebook links can't carry a start time. */
export function supportsStartTime(provider: VideoProvider): boolean {
  return provider === "youtube" || provider === "vimeo";
}

/** Thumbnail for watch, short, embed, live and youtu.be links. */
function youtubeThumbnail(url: URL): string | null {
  const id =
    url.hostname.toLowerCase() === "youtu.be"
      ? url.pathname.split("/")[1]
      : (url.searchParams.get("v") ?? url.pathname.match(/^\/(?:shorts|embed|live)\/([^/]+)/)?.[1]);
  return id && /^[\w-]{6,20}$/.test(id) ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
}

/** A video example ready to render: everything a card shows, derived once. */
export interface ResolvedVideoExample extends BriefingVideoExample {
  provider: VideoProvider;
  /** "YouTube", "Vimeo", ... */
  label: string;
  /** The https link to open, with the start time applied where supported. */
  href: string;
  /** YouTube only; other providers have no public thumbnail url. */
  thumbnail: string | null;
  /** "Starts at 1:23", or "Watch from 1:23" when the link can't jump there. */
  startLabel: string | null;
}

function resolveVideoExample(example: BriefingVideoExample): ResolvedVideoExample | null {
  const url = parseHttpUrl(example.url);
  const provider = url && providerOf(url);
  if (!url || !provider) return null;
  url.protocol = "https:";
  const start = example.start_seconds;
  // Shorts ignore a start time; embeds take `start` instead of `t`.
  const isShort = provider === "youtube" && url.pathname.startsWith("/shorts/");
  const canJump = supportsStartTime(provider) && !isShort;
  if (start && canJump) {
    if (provider === "vimeo") url.hash = `t=${start}s`;
    else url.searchParams.set(url.pathname.startsWith("/embed/") ? "start" : "t", String(start));
  }
  return {
    ...example,
    provider,
    label: VIDEO_PROVIDER_LABELS[provider],
    href: url.toString(),
    thumbnail: provider === "youtube" ? youtubeThumbnail(url) : null,
    startLabel: start ? `${canJump ? "Starts at" : "Watch from"} ${formatStartTime(start)}` : null,
  };
}

/**
 * The stored `video_examples` Json (or the editor's typed list) → the
 * rows crew get to see. Unsupported links are dropped.
 */
export function resolveVideoExamples(json: unknown): ResolvedVideoExample[] {
  return parseVideoExamples(json)
    .map(resolveVideoExample)
    .filter((v): v is ResolvedVideoExample => v !== null);
}

/** 83 → "1:23", 3725 → "1:02:05". */
export function formatStartTime(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${m}:${sec}`;
}

/**
 * Producer input → seconds. Accepts "83", "1:23" and "1:02:05".
 * Empty → null (start at the beginning); unreadable → undefined.
 */
export function parseStartTime(input: string): number | null | undefined {
  const trimmed = input.trim();
  if (!trimmed) return null;
  // Two-digit segments after a colon: "1:5" is ambiguous (1:05 or 1:50?).
  if (!/^\d+(:\d{2}){0,2}$/.test(trimmed)) return undefined;
  const parts = trimmed.split(":").map(Number);
  if (parts.slice(1).some((p) => p > 59)) return undefined;
  return parts.reduce((total, part) => total * 60 + part, 0);
}

/**
 * Parse the loosely-typed `video_examples` Json into a typed array.
 * Defensive: malformed rows degrade rather than throw on crew devices.
 */
export function parseVideoExamples(json: unknown): BriefingVideoExample[] {
  if (!Array.isArray(json)) return [];
  return json
    .filter((v): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v))
    .map((v, index) => ({
      // A stable fallback so every caller can key lists on id.
      id: typeof v.id === "string" && v.id ? v.id : `video-${index}`,
      url: typeof v.url === "string" ? v.url : "",
      note: typeof v.note === "string" ? v.note : "",
      start_seconds:
        typeof v.start_seconds === "number" &&
        Number.isFinite(v.start_seconds) &&
        v.start_seconds > 0
          ? Math.floor(v.start_seconds)
          : null,
    }));
}

/**
 * The rows worth saving: anything the producer typed in. An unsupported
 * link is kept (crew don't see it, resolveVideoExamples drops it) so the
 * producer's note survives until they fix the link.
 */
export function filledVideoExamples(examples: BriefingVideoExample[]): BriefingVideoExample[] {
  return examples.filter((v) => v.url.trim() !== "" || v.note.trim() !== "");
}
